import React, { ReactNode } from 'react';
import { useFormContext } from 'react-hook-form-mui';
import { DeviationQuestionDto, DeviationQuestionUserResponseDto } from 'src/generated/services/TFinancialApi';
import { useQuestionnaireData } from './hooks/useQuestionnaireData';
import { useQuestionnaireFields } from './hooks/useQuestionnaireFields';
import { QuestionField } from './components/QuestionField';

interface QuestionnaireFormBuilderProps {
  questions: DeviationQuestionDto[];
  answers: DeviationQuestionUserResponseDto[];
  additionalDefaultValues?: Record<string, any>;
  isEditable?: boolean;
  deviationId?: string; // Add deviationId prop
  setupCustomInputs?: (noticeEl: ReactNode, inputsEl: ReactNode, disabled?: boolean) => ReactNode;
}

/**
 * Form builder component that renders dynamic questionnaire fields
 * This component must be used inside a FormContainer to access form context
 */
export const QuestionnaireFormBuilder: React.FC<QuestionnaireFormBuilderProps> = ({
  questions,
  answers,
  additionalDefaultValues = {},
  isEditable = true,
  deviationId, // Add deviationId parameter
  setupCustomInputs,
}) => {
  // Get form context from FormContainer
  const formContext = useFormContext();
  const formValues = formContext.watch();

  // Process questionnaire data
  const { visibleFieldConfigs, defaultValues } = useQuestionnaireData({
    questions,
    answers,
    formValues,
    additionalDefaultValues,
  });

  // Convert field configs to renderable field definitions
  const fieldDefinitions = useQuestionnaireFields({
    fieldConfigs: visibleFieldConfigs,
    isReadOnly: !isEditable,
  });

  // Update form default values if needed
  React.useEffect(() => {
    const currentValues = formContext.getValues();
    const hasNewDefaults = Object.keys(defaultValues).some((key) => currentValues[key] === undefined && defaultValues[key] !== undefined);

    if (hasNewDefaults) {
      Object.entries(defaultValues).forEach(([key, value]) => {
        if (currentValues[key] === undefined) {
          formContext.setValue(key, value);
        }
      });
    }
  }, [defaultValues, formContext]);

  if (!fieldDefinitions || fieldDefinitions.length === 0) {
    return null;
  }

  return (
    <div className='flex flex-col gap-6'>
      {fieldDefinitions.map((fieldDefinition) => (
        <QuestionField
          key={fieldDefinition.name}
          fieldDefinition={fieldDefinition}
          disabled={!isEditable}
          deviationId={deviationId}
          setupCustomInputs={setupCustomInputs}
        />
      ))}
    </div>
  );
};
